import React from 'react';

import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { usePatchReferenceRate } from 'src/features/referenceRates/api/usePatchReferenceRate';
import { LIST_REFERENCE_RATES_RESOURCE } from 'src/features/referenceRates/api/useListReferenceRates';
import { REFERENCE_RATE_RESOURCE } from 'src/features/referenceRates/api/useGetReferenceRate';
import { referenceRatesMessages } from 'src/features/referenceRates/utils/messages';
import { ReferenceRate } from 'src/features/referenceRates/utils/types';
import ReferenceRateFormModal from 'src/features/referenceRates/components/forms/ReferenceRateFormModal';

interface UpdateReferenceRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRate: ReferenceRate;
    children?: React.ReactNode;
}

function UpdateReferenceRateFormModal<TData>({
    className,
    children,
    selectedReferenceRate,
}: UpdateReferenceRateFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = usePatchReferenceRate({
        onSuccess: (referenceRate) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [REFERENCE_RATE_RESOURCE] });
            toast({
                title: formatMessage(referenceRatesMessages.updateReferenceRateSuccess),
                description: formatMessage(referenceRatesMessages.updateReferenceRateSuccessDescription, {
                    entity: referenceRate.name,
                }),
            });
        },
    });

    return (
        <ReferenceRateFormModal
            selectedReferenceRate={selectedReferenceRate}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedReferenceRate.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(referenceRatesMessages.referenceRate).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(referenceRatesMessages.editReferenceRateSubtitle, {
                entity: selectedReferenceRate.name,
            })}
        >
            {children}
        </ReferenceRateFormModal>
    );
}

export default UpdateReferenceRateFormModal;
